/**
 * Meta Lead Ads dashboard section — a dedicated card for the admin console.
 * Renders 12 stat tiles, a filter bar (date/campaign/ad-set/ad-name/city),
 * and an expandable row per lead with a full timeline of the drip journey.
 *
 * Extracted from AdminDashboard.jsx to keep the parent under 1600 lines.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Sparkles, ChevronDown, ChevronRight, CheckCircle2, Circle, MessageCircle } from "lucide-react";
import { api } from "../lib/api";

const STAT_TILES = [
  { key: "total_leads",            label: "Meta leads",         tone: "indigo"  },
  { key: "whatsapp_sent",          label: "WhatsApp sent",      tone: "sky"     },
  { key: "registration_completed", label: "Registered",         tone: "blue"    },
  { key: "pending_registration",   label: "Pending reg.",       tone: "amber"   },
  { key: "reports_uploaded",       label: "Reports uploaded",   tone: "teal"    },
  { key: "pending_report_upload",  label: "Pending upload",     tone: "orange"  },
  { key: "cases_submitted",        label: "Cases submitted",    tone: "cyan"    },
  { key: "opinion_completed",      label: "Opinion done",       tone: "emerald" },
  { key: "free_consult_used",      label: "Free consult used",  tone: "violet"  },
  { key: "conversion_rate_pct",    label: "Conversion %",       tone: "green", suffix: "%" },
  { key: "avg_registration_time_hours", label: "Avg reg. time (h)", tone: "slate" },
  { key: "avg_report_upload_time_hours", label: "Avg upload time (h)", tone: "slate" },
];

// Tailwind's JIT can't detect dynamic class names, so pre-declare tones inline.
const TONE_CLASSES = {
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50/40",  text: "text-indigo-700"  },
  sky:     { border: "border-sky-200",     bg: "bg-sky-50/40",     text: "text-sky-700"     },
  blue:    { border: "border-blue-200",    bg: "bg-blue-50/40",    text: "text-blue-700"    },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50/40",   text: "text-amber-700"   },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50/40",    text: "text-teal-700"    },
  orange:  { border: "border-orange-200",  bg: "bg-orange-50/40",  text: "text-orange-700"  },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50/40",    text: "text-cyan-700"    },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50/40", text: "text-emerald-700" },
  violet:  { border: "border-violet-200",  bg: "bg-violet-50/40",  text: "text-violet-700"  },
  green:   { border: "border-green-200",   bg: "bg-green-50/40",   text: "text-green-700"   },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50/60",   text: "text-slate-700"   },
};

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtShort = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—");

function TimelineRail({ steps }) {
  return (
    <ol className="mt-2 grid grid-cols-1 sm:grid-cols-7 gap-3">
      {steps.map((s, i) => {
        const done = !!s.at;
        return (
          <li key={s.key} className="flex sm:block items-center gap-2" data-testid={`timeline-${s.key}`}>
            <div className="flex items-center gap-2">
              {done
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
              <div className={`text-xs font-semibold ${done ? "text-slate-800" : "text-slate-400"}`}>
                {s.label}
              </div>
            </div>
            <div className={`text-[10px] mt-0.5 ml-6 sm:ml-0 ${done ? "text-slate-500" : "text-slate-300"}`}>
              {s.at ? fmtShort(s.at) : "—"}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function MetaLeadsSection() {
  const [data, setData] = useState({ rows: [], stats: {} });
  const [filters, setFilters] = useState({ date_from: "", date_to: "", campaign_id: "", ad_set_id: "", ad_name: "", city: "" });
  const [dropdowns, setDropdowns] = useState({ campaigns: [], ad_sets: [], ad_names: [], cities: [] });
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/meta-leads${query ? `?${query}` : ""}`)
      .then((r) => setData(r.data || { rows: [], stats: {} }))
      .catch(() => setData({ rows: [], stats: {} }))
      .finally(() => setLoading(false));
  }, [query]);

  useEffect(() => {
    api.get("/admin/meta-leads/filters")
      .then((r) => setDropdowns(r.data || {}))
      .catch(() => {});
  }, []);

  const toggle = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  return (
    <>
      <h2 className="font-heading text-2xl font-semibold text-slate-900 mt-10 flex items-center gap-2" data-testid="admin-meta-leads-section">
        <Sparkles className="w-5 h-5 text-indigo-600" /> Meta Lead Ads
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        Leads captured from Facebook / Instagram Lead Ad forms. Auto-matched to registered patients by phone or email. Follows a 5-touch WhatsApp drip: welcome → 6h → 24h → 72h → upload nudge. (Auto free-consult grant retired Feb 28, 2026 — use the admin "Grant free consult" tool below for one-off support cases.)
      </p>

      {/* Filter bar */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="meta-leads-filters">
        <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} placeholder="From" data-testid="meta-filter-date-from" />
        <Input type="date" value={filters.date_to}   onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}   placeholder="To"   data-testid="meta-filter-date-to" />
        <select value={filters.campaign_id} onChange={(e) => setFilters({ ...filters, campaign_id: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="meta-filter-campaign">
          <option value="">All campaigns</option>
          {dropdowns.campaigns?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.ad_set_id} onChange={(e) => setFilters({ ...filters, ad_set_id: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="meta-filter-adset">
          <option value="">All ad sets</option>
          {dropdowns.ad_sets?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input value={filters.ad_name} onChange={(e) => setFilters({ ...filters, ad_name: e.target.value })} placeholder="Ad name…" data-testid="meta-filter-adname" />
        <select value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700" data-testid="meta-filter-city">
          <option value="">All cities</option>
          {dropdowns.cities?.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Stats grid — 12 tiles */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="meta-leads-stats">
        {STAT_TILES.map((tile) => {
          const raw = data.stats?.[tile.key];
          const val = raw === null || raw === undefined ? "—" : raw;
          const tone = TONE_CLASSES[tile.tone] || TONE_CLASSES.slate;
          return (
            <Card key={tile.key} className={`p-3 ${tone.border} ${tone.bg}`} data-testid={`meta-stat-${tile.key}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-wide ${tone.text}`}>{tile.label}</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">
                {val}{tile.suffix && val !== "—" ? tile.suffix : ""}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Leads table with expandable timeline */}
      <Card className="mt-4 p-0 border-slate-200 overflow-hidden" data-testid="admin-meta-leads-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-3 py-3 font-semibold">When</th>
                <th className="text-left px-3 py-3 font-semibold">Name</th>
                <th className="text-left px-3 py-3 font-semibold">Phone / Email</th>
                <th className="text-left px-3 py-3 font-semibold">City</th>
                <th className="text-left px-3 py-3 font-semibold">WA</th>
                <th className="text-left px-3 py-3 font-semibold">Registered</th>
                <th className="text-left px-3 py-3 font-semibold">Reports</th>
                <th className="text-left px-3 py-3 font-semibold">Doctor</th>
                <th className="text-left px-3 py-3 font-semibold">Opinion</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="px-4 py-6 text-sm text-slate-500 text-center">Loading Meta leads…</td></tr>
              )}
              {!loading && (!data.rows || data.rows.length === 0) && (
                <tr><td colSpan={10} className="px-4 py-6 text-sm text-slate-500 text-center">No Meta Lead Ads matching filters. Set up your Facebook/Instagram Lead Ad form and connect the webhook (<code className="text-xs bg-slate-100 px-1 py-0.5 rounded">docs/meta-lead-ads-setup.md</code>).</td></tr>
              )}
              {!loading && data.rows?.map((r) => (
                <React.Fragment key={r.lead_id}>
                  <tr className="border-t border-slate-200 hover:bg-slate-50/70 cursor-pointer" onClick={() => toggle(r.lead_id)} data-testid={`meta-lead-row-${r.lead_id}`}>
                    <td className="px-1 py-3">
                      {expanded[r.lead_id]
                        ? <ChevronDown className="w-4 h-4 text-slate-400 ml-2" />
                        : <ChevronRight className="w-4 h-4 text-slate-400 ml-2" />}
                    </td>
                    <td className="px-3 py-3 text-slate-500 whitespace-nowrap text-xs">{fmtDT(r.lead_created_at)}</td>
                    <td className="px-3 py-3 font-semibold text-slate-800">{r.name || "—"}</td>
                    <td className="px-3 py-3 text-xs">
                      {r.phone && <div className="font-mono text-blue-700">{r.phone}</div>}
                      {r.email && <div className="text-slate-500 truncate max-w-[220px]" title={r.email}>{r.email}</div>}
                    </td>
                    <td className="px-3 py-3 text-slate-600 text-xs">{r.city || "—"}</td>
                    <td className="px-3 py-3">
                      {r.welcome_sent_at ? <MessageCircle className="w-4 h-4 text-emerald-600" title={`Welcome sent ${fmtDT(r.welcome_sent_at)}`} /> : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-3 py-3">
                      {r.registration_completed
                        ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Yes</span>
                        : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">Pending</span>}
                    </td>
                    <td className="px-3 py-3">
                      {r.reports_uploaded
                        ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">Uploaded</span>
                        : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">Pending</span>}
                    </td>
                    <td className="px-3 py-3 text-slate-700 text-xs">{r.doctor_assigned || "—"}</td>
                    <td className="px-3 py-3">
                      {r.opinion_completed
                        ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">Delivered</span>
                        : <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">—</span>}
                    </td>
                  </tr>
                  {expanded[r.lead_id] && (
                    <tr className="bg-slate-50/60 border-t border-slate-100" data-testid={`meta-lead-timeline-${r.lead_id}`}>
                      <td></td>
                      <td colSpan={9} className="px-4 py-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Journey timeline</div>
                        <TimelineRail steps={r.timeline || []} />
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-500">
                          <div><b className="text-slate-700">Campaign:</b> {r.campaign_id || "—"}</div>
                          <div><b className="text-slate-700">Ad set:</b> {r.ad_set_id || "—"}</div>
                          <div><b className="text-slate-700">Ad name:</b> {r.ad_name || "—"}</div>
                          <div><b className="text-slate-700">Meta lead ID:</b> <span className="font-mono">{r.meta_lead_id || "—"}</span></div>
                          <div><b className="text-slate-700">Reminder 6h:</b> {fmtDT(r.reminder_6h_sent_at)}</div>
                          <div><b className="text-slate-700">Reminder 24h:</b> {fmtDT(r.reminder_24h_sent_at)}</div>
                          <div><b className="text-slate-700">Reminder 72h:</b> {fmtDT(r.reminder_72h_sent_at)}</div>
                          <div><b className="text-slate-700">Upload nudge:</b> {fmtDT(r.upload_reminder_sent_at)}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
